import request from './request';
import endPoint from './endPoint';

/**
 * Service de gestion des transactions de paiement
 * Compatible avec les méthodes de paiement du SubscriptionController Laravel
 */
export class PaymentTransactionService {
  constructor() {
    this.baseUrl = `/payment-transactions`;
  }

  /**
   * Méthodes de paiement supportées
   */
  getPaymentMethods() {
    return [
      { value: 'orange_money', label: 'Orange Money', icon: '🟠', color: 'warning' },
      { value: 'moov_money', label: 'Moov Money', icon: '🔵', color: 'primary' },
      { value: 'mtn_money', label: 'MTN Mobile Money', icon: '🟡', color: 'warning' },
      { value: 'card', label: 'Carte Bancaire', icon: '💳', color: 'info' },
      { value: 'bank_transfer', label: 'Virement Bancaire', icon: '🏦', color: 'secondary' },
      { value: 'cash', label: 'Espèces', icon: '💵', color: 'success' }
    ];
  }

  /**
   * Statuts des transactions
   */
  getTransactionStatuses() {
    return [
      { value: 'pending', label: 'En attente', color: 'warning' },
      { value: 'completed', label: 'Terminé', color: 'success' },
      { value: 'failed', label: 'Échec', color: 'danger' },
      { value: 'cancelled', label: 'Annulé', color: 'secondary' },
      { value: 'refunded', label: 'Remboursé', color: 'info' }
    ];
  }

  /**
   * Récupérer toutes les transactions avec filtres et pagination
   */
  async getTransactions(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Paramètres de pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      
      // Filtres
      if (params.payment_method) queryParams.append('payment_method', params.payment_method);
      if (params.status) queryParams.append('status', params.status);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.search) queryParams.append('search', params.search);
      if (params.subscription_id) queryParams.append('subscription_id', params.subscription_id);
      
      // Tris
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
      
      const url = queryParams.toString() ? `${this.baseUrl}?${queryParams}` : this.baseUrl;
      const response = await request.get(url);
      
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des transactions:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer une transaction par son ID
   */
  async getTransaction(id) {
    try {
      const response = await request.get(`${this.baseUrl}/${id}`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la récupération de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Créer une nouvelle transaction
   */
  async createTransaction(transactionData) {
    try {
      const response = await request.post(this.baseUrl, transactionData);
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de la création de la transaction:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Mettre à jour une transaction
   */
  async updateTransaction(id, updateData) {
    try {
      const response = await request.put(`${this.baseUrl}/${id}`, updateData);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Mettre à jour le statut d'une transaction
   */
  async updateTransactionStatus(id, status) {
    try {
      const response = await request.put(`${this.baseUrl}/${id}/status`, { status });
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du statut de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Approuver une transaction en attente
   */
  async approveTransaction(id) {
    try {
      const response = await request.post(`${this.baseUrl}/${id}/approve`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de l'approbation de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Rejeter une transaction
   */
  async rejectTransaction(id, reason = '') {
    try {
      const response = await request.post(`${this.baseUrl}/${id}/reject`, { reason });
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors du rejet de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Rembourser une transaction
   */
  async refundTransaction(id, refundData = {}) {
    try {
      const response = await request.post(`${this.baseUrl}/${id}/refund`, refundData);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors du remboursement de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Supprimer une transaction (si autorisé)
   */
  async deleteTransaction(id) {
    try {
      const response = await request.delete(`${this.baseUrl}/${id}`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la suppression de la transaction ${id}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer les statistiques des transactions
   */
  async getTransactionStats(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.payment_method) queryParams.append('payment_method', params.payment_method);
      
      const url = queryParams.toString() ? 
        `${this.baseUrl}/stats?${queryParams}` : 
        `${this.baseUrl}/stats`;
      
      const response = await request.get(url);
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Exporter les transactions
   */
  async exportTransactions(format = 'xlsx', filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      
      // Ajouter les filtres
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key]);
      });
      
      const response = await request.get(
        `${this.baseUrl}/export?${queryParams}`,
        { responseType: 'blob' }
      );
      
      return response;
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer les transactions par abonnement
   */
  async getTransactionsBySubscription(subscriptionId, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.status) queryParams.append('status', params.status);
      
      const url = queryParams.toString() ? 
        `${endPoint.subscriptions}/${subscriptionId}/transactions?${queryParams}` :
        `${endPoint.subscriptions}/${subscriptionId}/transactions`;
      
      const response = await request.get(url);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la récupération des transactions pour l'abonnement ${subscriptionId}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Récupérer les transactions par utilisateur
   */
  async getTransactionsByUser(userId, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.status) queryParams.append('status', params.status);
      if (params.payment_method) queryParams.append('payment_method', params.payment_method);
      
      const url = queryParams.toString() ? 
        `${endPoint.baseUrl}/users/${userId}/transactions?${queryParams}` :
        `${endPoint.baseUrl}/users/${userId}/transactions`;
      
      const response = await request.get(url);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la récupération des transactions pour l'utilisateur ${userId}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Initier un paiement mobile money
   */
  async initiateMobilePayment(paymentData) {
    try {
      const response = await request.post(`${this.baseUrl}/mobile-payment/initiate`, paymentData);
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors de l\'initiation du paiement mobile:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Vérifier le statut d'un paiement mobile
   */
  async checkMobilePaymentStatus(transactionId) {
    try {
      const response = await request.get(`${this.baseUrl}/${transactionId}/mobile-payment/status`);
      return this.formatResponse(response);
    } catch (error) {
      console.error(`Erreur lors de la vérification du statut pour ${transactionId}:`, error);
      throw this.formatError(error);
    }
  }

  /**
   * Traitement des webhooks de paiement
   */
  async processPaymentWebhook(webhookData) {
    try {
      const response = await request.post(`${this.baseUrl}/webhook/payment`, webhookData);
      return this.formatResponse(response);
    } catch (error) {
      console.error('Erreur lors du traitement du webhook:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Utilitaires pour le formatage des données
   */
  formatPrice(amount, currency = 'XOF') {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + currency;
  }

  formatDate(dateString) {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getPaymentMethodInfo(method) {
    const methods = this.getPaymentMethods();
    return methods.find(pm => pm.value === method) || 
           { label: method, icon: '💳', color: 'secondary' };
  }

  getStatusInfo(status) {
    const statuses = this.getTransactionStatuses();
    return statuses.find(s => s.value === status) || 
           { label: status, color: 'secondary' };
  }

  /**
   * Validation des données de transaction
   */
  validateTransactionData(data) {
    const errors = [];
    
    if (!data.subscription_id) {
      errors.push('L\'ID de l\'abonnement est requis');
    }
    
    if (!data.amount || data.amount <= 0) {
      errors.push('Le montant doit être supérieur à 0');
    }
    
    if (!data.payment_method) {
      errors.push('La méthode de paiement est requise');
    }
    
    const validMethods = this.getPaymentMethods().map(m => m.value);
    if (data.payment_method && !validMethods.includes(data.payment_method)) {
      errors.push('Méthode de paiement invalide');
    }
    
    const validStatuses = this.getTransactionStatuses().map(s => s.value);
    if (data.status && !validStatuses.includes(data.status)) {
      errors.push('Statut de transaction invalide');
    }
    
    return errors;
  }

  /**
   * Calculer les totaux et statistiques
   */
  calculateTransactionStats(transactions) {
    if (!Array.isArray(transactions)) return {};
    
    const stats = {
      total_count: transactions.length,
      total_amount: 0,
      completed_amount: 0,
      pending_count: 0,
      completed_count: 0,
      failed_count: 0,
      cancelled_count: 0,
      refunded_count: 0,
      refunded_amount: 0,
      by_payment_method: {},
      by_currency: {}
    };
    
    transactions.forEach(transaction => {
      const amount = parseFloat(transaction.amount) || 0;
      stats.total_amount += amount;
      
      // Compter par statut
      switch (transaction.status) {
        case 'pending':
          stats.pending_count++;
          break;
        case 'completed':
          stats.completed_count++;
          stats.completed_amount += amount;
          break;
        case 'failed':
          stats.failed_count++;
          break;
        case 'cancelled':
          stats.cancelled_count++;
          break;
        case 'refunded':
          stats.refunded_count++;
          stats.refunded_amount += amount;
          break;
        default:
          // Autres statuts
          break;
      }
      
      // Compter par méthode de paiement
      const method = transaction.payment_method;
      if (!stats.by_payment_method[method]) {
        stats.by_payment_method[method] = { count: 0, amount: 0 };
      }
      stats.by_payment_method[method].count++;
      if (transaction.status === 'completed') {
        stats.by_payment_method[method].amount += amount;
      }
      
      // Compter par devise
      const currency = transaction.currency || 'XOF';
      if (!stats.by_currency[currency]) {
        stats.by_currency[currency] = { count: 0, amount: 0 };
      }
      stats.by_currency[currency].count++;
      if (transaction.status === 'completed') {
        stats.by_currency[currency].amount += amount;
      }
    });
    
    return stats;
  }

  /**
   * Formater les réponses de l'API
   */
  formatResponse(response) {
    if (response.data?.data) {
      return response.data;
    }
    if (response.data) {
      return { success: true, data: response.data };
    }
    return { success: true, data: response };
  }

  /**
   * Formater les erreurs
   */
  formatError(error) {
    if (error.response?.data) {
      return {
        success: false,
        message: error.response.data.message || 'Erreur inconnue',
        errors: error.response.data.errors || {},
        status: error.response.status
      };
    }
    return {
      success: false,
      message: error.message || 'Erreur de connexion',
      errors: {},
      status: 0
    };
  }
}

// Instance par défaut
const paymentTransactionService = new PaymentTransactionService();
export default paymentTransactionService;
